import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useNow } from '@/hooks/useNow';
import { encode } from '@/hooks/useQueryState';
import type { DeepUnknown } from '@/types/deep-unknown';

import { ContractSchema } from '../entities/contract';
import { getContractStatus } from '../utils/get-contract-status';

export namespace ContractsQuery {
  export const key = () => ['customer-info', 'contracts'];

  export const OutputSchema = z.object({
    contracts: z.array(ContractSchema),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    data: FeApi.SitesListContractInfoViewOutput,
    now: Date,
  ): DeepUnknown<Output> {
    return {
      contracts: data.sites_contract_info.map((contract) => {
        const startDate = contract.contract_start_date
          ? new Date(contract.contract_start_date)
          : undefined;
        const endDate = contract.contract_end_date
          ? new Date(contract.contract_end_date)
          : undefined;
        const status = getContractStatus(startDate, endDate, now);
        return {
          id: encode([contract.site_accure_id, startDate, endDate]),
          assetId: contract.site_accure_id,
          assetName: contract.site_name,
          boltTier: contract.bolt_tier ?? undefined,
          startDate,
          endDate,
          status,
        };
      }),
    };
  }

  function useQueryFn() {
    const fetchResult = useInjectFeApiContext(
      FeApi.customerInfosV1SitesListContractInfoRetrieve,
    );
    const now = useNow();

    return async function queryFn() {
      const contractList = await ok(fetchResult({}));
      return OutputSchema.parseAsync(outputMapper(contractList, now));
    };
  }

  export function useQuery() {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(),
      queryFn: () => queryFn(),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(),
        queryFn: () => queryFn(),
      });
    };
  }
}
